'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { getFriendlyErrorMessage } from '@/utils/errorMessages';

interface SecuritySectionProps {
    onSignOut: () => Promise<void>;
    onDeleteAccount: () => Promise<{ error: any }>;
}

export default function SecuritySection({ onSignOut, onDeleteAccount }: SecuritySectionProps) {
    const [showSignOutDialog, setShowSignOutDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleDelete = async () => {
        setLoading(true);
        setError(null);
        try {
            const { error: deleteError } = await onDeleteAccount();
            if (deleteError) {
                const { message } = getFriendlyErrorMessage(deleteError);
                setError(message);
                setShowDeleteDialog(false);
            }
        } catch (err: any) {
            const { message } = getFriendlyErrorMessage(err);
            setError(message);
            setShowDeleteDialog(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-3">
                    <Icon name="ExclamationTriangleIcon" className="text-red-600 flex-shrink-0" size={20} />
                    <p>{error}</p>
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-200 bg-gray-50/50">
                    <h2 className="text-xl font-bold text-gray-900">Security & Account</h2>
                    <p className="text-sm text-gray-500 mt-1">Manage your account access and data.</p>
                </div>

                <div className="p-6 space-y-6">
                    {/* Sign Out Row */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-50/80 transition-colors">
                        <div className="flex items-start gap-4">
                            <div className="p-2 bg-white rounded-lg border border-gray-200 text-gray-500 shadow-sm">
                                <Icon name="ArrowRightOnRectangleIcon" size={24} />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900">Sign Out</h3>
                                <p className="text-sm text-gray-500 mt-0.5">Securely log out of your current session on this device.</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowSignOutDialog(true)}
                            className="px-5 py-2.5 border border-gray-300 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400 transition-all shadow-sm whitespace-nowrap"
                        >
                            Sign Out
                        </button>
                    </div>

                    {/* Delete Account Row */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-lg border border-red-100 bg-red-50/50 hover:bg-red-50 transition-colors">
                        <div className="flex items-start gap-4">
                            <div className="p-2 bg-white rounded-lg border border-red-100 text-red-500 shadow-sm">
                                <Icon name="TrashIcon" size={24} />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900">Delete Account</h3>
                                <p className="text-sm text-gray-500 mt-0.5">Permanently remove your account and all associated data.</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowDeleteDialog(true)}
                            className="px-5 py-2.5 bg-white border border-red-200 text-red-600 font-medium rounded-lg hover:bg-red-600 hover:text-white hover:border-red-600 transition-all shadow-sm whitespace-nowrap"
                        >
                            Delete Account
                        </button>
                    </div>
                </div>
            </div>

            {/* Dialogs */}
            <ConfirmDialog
                isOpen={showSignOutDialog}
                onClose={() => setShowSignOutDialog(false)}
                onConfirm={async () => {
                    await onSignOut();
                    setShowSignOutDialog(false);
                }}
                title="Sign Out"
                message="Are you sure you want to sign out?"
                confirmText="Sign Out"
                variant="default"
            />

            <ConfirmDialog
                isOpen={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDelete}
                title="Delete Account"
                message="This action is irreversible. All your data, including saved pets and messages, will be permanently deleted. Are you sure you want to proceed?"
                confirmText="Yes, Delete My Account"
                variant="danger"
                isLoading={loading}
            />
        </div>
    );
}
